<template>
  <v-card outlined>
    <v-container class="px-1 py-1" fluid>
      <v-card-title class="pt-2 pb-0">
        {{ $t('ELE0028') }}
      </v-card-title>
      <v-card-text>
        <ValidationObserver ref="observer" v-slot="{ invalid }">
          <v-row>
            <v-col cols="3">
              <TextFieldInput :label="$t('ELE0031')" v-model="searchCondition.partCd" rules="min:3" />
            </v-col>
            <v-col cols="3">
              <TextFieldInput :label="$t('ELE0032')" v-model="searchCondition.partName" rules="min:3" />
            </v-col>
            <v-col cols="3">
              <TextFieldInput :label="$t('ELE0224')" v-model="searchCondition.model" rules="min:3" />
            </v-col>
            <v-col cols="3">
              <PartGroupInput v-model="searchCondition.partGroupCd" />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <CompanyInput
                :label="$t('ELE0134')"
                v-model="searchCondition.manufacturerCd"
                :companyCatCd="enumCompanyCatCd.SUPPLIER"
              />
            </v-col>
            <v-col cols="3">
              <InspFilteringInput :label="$t('ELE0290')" v-model="searchCondition.inspFilteringGroupCd" />
            </v-col>
          </v-row>
          <v-row dense>
            <v-col>
              <span class="d-flex flex-row space-between">
                <Checkbox :label="$t('ELE0022')" v-model="searchCondition.validOnly" />
                <v-spacer />
                <BtnSearch class="mt-2" @click="onClickSearch" :disabled="invalid || isFieldAllEmpty" />
              </span>
            </v-col>
          </v-row>
        </ValidationObserver>
      </v-card-text>
      <v-card outlined>
        <v-card-title class="pt-2 pb-1">
          {{ $t('ELE0023') }}
          <v-spacer />
          <BtnAdd @click="addItem" />
        </v-card-title>
        <v-data-table :headers="headersComputed" :items="items" dense>
          <template v-slot:[`item.action`]="{ item }">
            <IconInquiry class="mr-2" @click="inquiryItem(item)" />
            <IconEdit class="mr-2" @click="editItem(item)" />
            <v-icon color="primary" @click="copyItem(item)">mdi-content-copy</v-icon>
          </template>
          <template v-slot:[`item.isValid`]="{ item }">
            {{ $t('isValid.' + item.isValid) }}
          </template>
          <template v-slot:[`item.isManagedMold`]="{ item }">
            {{ $t('isManagedMold.' + item.isManagedMold) }}
          </template>
        </v-data-table>
      </v-card>
    </v-container>
    <PartDialog
      v-if="item !== null"
      :dialog.sync="partDialog"
      :mode="mode"
      :partCd.sync="item.partCd"
      :isShownMold="isShownMold"
      :isManagedMold.sync="item.isManagedMold"
      :moldNo.sync="item.moldNo"
      :partRev.sync="item.partRev"
      :partName.sync="item.partName"
      :model.sync="item.model"
      :partCatCd.sync="item.partCatCd"
      :partGroupCd.sync="item.partGroupCd"
      :measInstCd.sync="item.measInstCd"
      :partInspTimingCd.sync="item.partInspTimingCd"
      :inspQtyStdCd.sync="item.inspQtyStdCd"
      :inspBasicQty.sync="item.inspBasicQty"
      :defectGroupCd.sync="item.defectGroupCd"
      :isValid.sync="item.isValid"
      :versionNo="item.versionNo"
      @change="onClickSearch"
    />
    <PartInquiryDialog
      v-if="itemInfo !== null"
      :dialog.sync="partInquiryDialog"
      :partCd="itemInfo.partCd"
      :partRev="itemInfo.partRev"
      :isShownMold="isShownMold"
    />
  </v-card>
</template>

<script>
import { API } from '@/plugins/api'
import { ModalMode, SystemParameterKey, CompanyCatCd } from '@/enum'
import cloneDeep from 'lodash/cloneDeep'
import { ValidationObserver } from 'vee-validate'

import APIUtils from '@/mixin/api-utils.js'
import Message from '@/mixin/message-utils.js'
import Loading from '@/mixin/loading-utils.js'
import DateUtils from '@/mixin/date-utils.js'
import StringUtils from '@/mixin/string-utils.js'

import TextFieldInput from '@/components/atoms/base/text-field/TextFieldInput'
import Checkbox from '@/components/atoms/base/checkbox/Checkbox'
import BtnSearch from '@/components/atoms/button/BtnSearch'
import BtnAdd from '@/components/atoms/button/BtnAdd'
import IconEdit from '@/components/atoms/icon/IconEdit'
import IconInquiry from '@/components/atoms/icon/IconInquiry'
import PartGroupInput from '@/components/atoms/select/PartGroupInput'
import CompanyInput from '@/components/atoms/select/CompanyInput'
import PartDialog from '@/components/organisms/master/dialog/PartDialog'
import PartInquiryDialog from '@/components/organisms/master/dialog/PartInquiryDialog'
import InspFilteringInput from '@/components/atoms/select/InspFilteringInput'

export default {
  name: 'PartForm',

  mixins: [APIUtils, Message, Loading, DateUtils, StringUtils],

  components: {
    ValidationObserver,
    TextFieldInput,
    Checkbox,
    BtnSearch,
    BtnAdd,
    IconEdit,
    IconInquiry,
    PartGroupInput,
    CompanyInput,
    PartDialog,
    PartInquiryDialog,
    InspFilteringInput,
  },

  data() {
    return {
      mode: '',
      partDialog: false,
      partInquiryDialog: false,
      item: null,
      itemInfo: null,
      items: [],
      headers: [
        { text: '', value: 'action', divider: true, sortable: false, width: '90px' },
        { text: this.$t('ELE0031'), value: 'partCd', divider: true },
        { text: this.$t('ELE0032'), value: 'partName', divider: true },
        { text: this.$t('ELE0051'), value: 'moldNo', divider: true },
        { text: this.$t('ELE0495'), value: 'isManagedMold', divider: true },
        { text: this.$t('ELE0283'), value: 'partRev', divider: true },
        { text: this.$t('ELE0224'), value: 'model', divider: true },
        { text: this.$t('ELE0289'), value: 'oqcInspFilteringName', divider: true },
        { text: this.$t('ELE0290'), value: 'iqcInspFilteringName', divider: true },
        { text: this.$t('ELE0014'), value: 'isValid', divider: true },
        { text: this.$t('ELE0029'), value: 'itemUpdatedBy', divider: true },
        { text: this.$t('ELE0039'), value: 'updaterName', divider: true },
        { text: this.$t('ELE0030'), value: 'itemUpdatedAt', divider: true },
      ],
      searchCondition: {
        partCd: '',
        partName: '',
        model: '',
        partGroupCd: '',
        manufacturerCd: '',
        inspFilteringGroupCd: '',
        validOnly: true,
      },
      emptyItem: {
        partCd: '',
        partRev: 0,
        partName: '',
        isManagedMold: true,
        moldNo: '',
        model: '',
        partCatCd: '',
        partGroupCd: '',
        measInstCd: '',
        partInspTimingCd: '',
        inspQtyStdCd: '',
        inspBasicQty: '',
        defectGroupCd: '',
        isValid: true,
        versionNo: 0,
      },
      isShownMold: false,
    }
  },

  props: {
    propsPartCd: {
      type: String,
      required: false,
      default: '',
    },
  },

  computed: {
    enumCompanyCatCd: () => CompanyCatCd,
    isFieldAllEmpty: {
      get() {
        return this.isAllEmpty(
          this.searchCondition.partCd,
          this.searchCondition.partName,
          this.searchCondition.model,
          this.searchCondition.partGroupCd,
          this.searchCondition.manufacturerCd,
          this.searchCondition.inspFilteringGroupCd,
        )
      },
    },
    headersComputed: {
      get() {
        return this.headers?.filter((item) => {
          if (item.value == 'moldNo' || item.value == 'isManagedMold') {
            return this.isShownMold
          }
          return true
        })
      },
    },
  },

  methods: {
    addItem() {
      this.mode = ModalMode.ADD
      this.item = cloneDeep(this.emptyItem)
      this.item.isManagedMold = this.isShownMold // 金型表示する場合、金型管理チェックの初期値をtrue
      this.partDialog = true
    },
    editItem(item) {
      this.mode = ModalMode.EDIT
      this.item = cloneDeep(item)
      this.partDialog = true
    },
    inquiryItem(item) {
      this.itemInfo = item
      this.partInquiryDialog = true
    },
    copyItem(item) {
      this.mode = ModalMode.COPY
      this.item = cloneDeep(item)
      this.item.partName = ''
      this.partDialog = true
    },
    async onClickSearch() {
      this.startLoading()
      try {
        if (!(await this.$refs.observer.validate()) || this.isFieldAllEmpty) return
        const params = {
          partCd: this.searchCondition.partCd,
          partName: this.searchCondition.partName,
          model: this.searchCondition.model,
          partGroupCd: this.searchCondition.partGroupCd,
          manufacturerCd: this.searchCondition.manufacturerCd,
          inspFilteringGroupCd: this.searchCondition.inspFilteringGroupCd,
          validOnly: this.searchCondition.validOnly,
          isLatestRev: true,
        }
        this.items = []
        const elements = await API.get('/master/parts', params)
        elements.forEach((element) => {
          element['updaterName'] = element['m_user']['userName']
          element['itemUpdatedAt'] = this.toLocalTime(element['itemUpdatedAt'])
          element['iqcInspFilteringName'] = element['iqcInspFiltering']['inspFilteringGroupName']
          element['oqcInspFilteringName'] = element['oqcInspFiltering']['inspFilteringGroupName']
          if (!element['inspBasicQty']) {
            element['inspBasicQty'] = ''
          }
          this.items.push(element)
        })
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0059') })))
      } finally {
        this.stopLoading()
      }
    },
    async setManagedMoldParameter() {
      try {
        this.startLoading()
        this.isShownMold = false

        const element = await API.get(`/master/systemParameter/${SystemParameterKey.IS_MANAGED_PART_BY_MOLD}`)
        if (element) {
          this.isShownMold = element.value == 'TRUE'
        }
      } catch (error) {
        if (error.response.status != 404) {
          this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0348') })))
        }
      } finally {
        this.stopLoading()
      }
    },
  },
  async mounted() {
    await this.setManagedMoldParameter()

    if (this.propsPartCd) {
      this.searchCondition.partCd = this.propsPartCd
      this.onClickSearch()
    }
  },
}
</script>
